import { createRoute } from "@tanstack/react-router";
import { PowCaptcha } from "@yanzheng/pow";
import { ShieldCheck } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Input } from "../components/ui.tsx";
import { PhoneInput } from "../components/phone-input.tsx";
import { useAuthForm } from "../lib/use-auth-form.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopLogin } from "./desktop/login.tsx";

/**
 * 登录 / 注册（手机版）。
 *
 * ⚠️ 逻辑全在 `useAuthForm()` 里，这里只管版面 —— 人机验证票的一次性、
 *    冷却倒计时这两处都出过 bug，抄两份等于把 bug 也抄一份。
 */
function Login() {
  const a = useAuthForm();

  return (
    <SubPage title={a.mode === "login" ? "登录" : "注册"}>
      <div className="px-4 py-6">
        {a.search.invite && a.mode === "register" && (
          <p className="mb-4 rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-700">
            你是通过专属链接进来的，注册后将由该商家为你服务
          </p>
        )}

        <form onSubmit={a.submit} className="space-y-3">
          {/*
            🔴 **注册时手机号带地区选择器**（老板指定：默认台湾 → 中国 → 其他），
               存进库的是 E.164（`+8613700137000`）。
            ⚠️ **登录框仍然是一个纯文本框**，不给地区选择器：库里存量的老账号
               是裸的 11 位（`13700137000`），而且大陆用户登录时就是习惯直接打
               那 11 位数字。服务端用一组候选值去匹配（原样 / 正规化 / +86 / +886），
               见 `auth/service.ts` 的 `identifierCandidates`。
               这里加了选择器反而会把老用户挡在门外。
          */}
          {a.mode === "register" ? (
            <PhoneInput value={a.phone} onChange={a.setPhone} />
          ) : (
            <Input
              label="账号"
              autoComplete="username"
              placeholder="请输入手机号"
              value={a.phone}
              onChange={(e) => a.setPhone(e.target.value)}
            />
          )}
          <Input
            label="密码"
            type="password"
            autoComplete={a.mode === "login" ? "current-password" : "new-password"}
            placeholder={a.mode === "register" ? "至少 8 位" : "请输入密码"}
            value={a.password}
            onChange={(e) => a.setPassword(e.target.value)}
          />

          {/* 🔴 密码框是遮住的，打错一个字自己看不见 —— 而这个平台上账号是有钱的。 */}
          {a.mode === "register" && (
            <Input
              label="确认密码"
              type="password"
              autoComplete="new-password"
              placeholder="再输入一次"
              value={a.password2}
              onChange={(e) => a.setPassword2(e.target.value)}
            />
          )}
          {a.mode === "register" && (
            <Input
              label="昵称（选填）"
              value={a.nickname}
              onChange={(e) => a.setNickname(e.target.value)}
            />
          )}

          {a.showCaptcha && (
            <div className="rounded-lg bg-neutral-50 px-3 py-2.5">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs text-ink-500">
                <ShieldCheck className="size-3.5" />
                为了账号安全，请完成验证后重试
              </p>
              {/*
                🔴 **注册页已经没有这一块了**（老板：「注册账号取消安全校验」）。
                   这里只剩**登录**那条路：服务端判定这个 IP 像在撞库时会回
                   `CAPTCHA_REQUIRED`，那时才补一道验证重试。
                   `showCaptcha` 现在就等于 `needCaptcha`，是服务端主动要的。
                ⚠️ key 绑手机号：换号码必须重出一道题，否则票绑的还是旧号码。
                ⚠️ 组件自己会算，用户零操作 —— 别加"点击验证"之类的引导。
              */}
              <PowCaptcha key={`${a.mode}-${a.phone}`} onSuccess={a.setCaptcha} />
            </div>
          )}

          {/*
            🔴 **错误提示不能把按钮挤下去。**
               出错时按钮整块往下跳一截，用户正要点的位置突然变成了别的东西 ——
               在手机上这是最容易点错的一种交互。
               所以这里**预留一行的高度**：有没有错误，按钮都在同一个位置。

            ⚠️ 用 `min-h` 不是固定 `h`：两行的错误文案要撑得开，
               撑开时按钮往下让位是合理的，一行时不该抖。
          */}
          <div className="min-h-[2.75rem]">
            {a.error && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5">
                <p className="text-[13px] leading-snug text-destructive break-anywhere">
                  {a.cooling
                    ? `尝试次数过多，请 ${a.left} 秒后再试`
                    : a.error instanceof Error
                      ? a.error.message
                      : "出了点问题"}
                </p>
              </div>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full" loading={a.busy} disabled={a.cooling}>
            {a.cooling ? `${a.left} 秒后可重试` : a.mode === "login" ? "登录" : "注册并登录"}
          </Button>
        </form>

        <button
          className="mt-4 w-full text-center text-sm text-brand-600"
          onClick={() => a.setMode(a.mode === "login" ? "register" : "login")}
        >
          {a.mode === "login" ? "还没有账号？去注册" : "已有账号？去登录"}
        </button>
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与逻辑两边共用）。 */
function LoginRoute() {
  return useIsDesktop() ? <DesktopLogin /> : <Login />;
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { redirect?: string; invite?: string; visitId?: number } => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
    invite: typeof s.invite === "string" ? s.invite : undefined,
    visitId: s.visitId ? Number(s.visitId) : undefined,
  }),
});
